import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { TabPanel, TabView, TabViewTabChangeParams } from 'primereact/tabview'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// local imports
import './CampusServices.scss'
import AuthUtil from 'utils/AuthUtil'
import { ICampusServiceExists } from 'service/CampusService'
import { DoctorList } from './health-service'
import { CoiffeurSalonList } from './coiffeur'
import { GymFilteredList } from './gym'
import { LactationRoomList } from './lactation-room'
import { ParkAreaList } from './park-area'
import { MeetingRoomList } from './meeting-room'
import { PoolVehicleList } from './pool-vehicles'

export default function OnCampusServices({
  campusVisible,
  redirectCommon,
}: {
  campusVisible: ICampusServiceExists
  redirectCommon: Dispatch<SetStateAction<number>>
}) {
  const { t } = useTranslation()
  const { search } = useLocation()
  const history = useHistory()
  const query = new URLSearchParams(search)
  const tabQuery = query.get('tab')
  const employeeSummary = AuthUtil.getEmployeeSummary()
  const {
    isCoiffeurServiceExist,
    isGymServiceExist,
    isHealthServiceExist,
    isLactationServiceExist,
    isParkAreaServiceExist,
    isMeetingServiceExist,
    isPoolVehicleServiceExist,
  } = campusVisible || {}

  /* Campus Service ExemptionList Check  */
  const hasHealth = AuthUtil.hasHealth(employeeSummary?.exemptionSpecList!)
  const hasGym = AuthUtil.hasGym(employeeSummary?.exemptionSpecList!)
  const hasCoiffeur = AuthUtil.hasCoiffeur(employeeSummary?.exemptionSpecList!)
  const hasLactation = AuthUtil.hasLactation(employeeSummary?.exemptionSpecList!)
  const hasMeeting = AuthUtil.hasMeeting(employeeSummary?.exemptionSpecList!)
  const hasParkArea = AuthUtil.hasParkArea(employeeSummary?.exemptionSpecList!)
  const hasPoolVehicle = AuthUtil.hasPoolVehicle(employeeSummary?.exemptionSpecList!)

  /* Set default active tab - according to available service indexes */
  const setTabIndex = () =>
    isHealthServiceExist && hasHealth
      ? 0
      : isGymServiceExist && hasGym
      ? 1
      : isCoiffeurServiceExist && hasCoiffeur
      ? 2
      : isLactationServiceExist && hasLactation
      ? 3
      : isParkAreaServiceExist && hasParkArea
      ? 4
      : isMeetingServiceExist && hasMeeting
      ? 5
      : 6
  const [activeIndex, setActiveIndex] = useState(() => setTabIndex())

  /* Park Area Redirect Control */
  useEffect(() => {
    if (!!tabQuery && tabQuery === 'park') setActiveIndex(4)
  }, [tabQuery])

  const handleTabChange = (e: TabViewTabChangeParams) => {
    if (!!tabQuery) history.replace('/home')
    setActiveIndex(e.index)
  }

  return (
    <InnerTabWrapper>
      <TabView
        renderActiveOnly
        className="on-campus-service-tab"
        activeIndex={activeIndex}
        onTabChange={handleTabChange}>
        {isHealthServiceExist && hasHealth && (
          <TabPanel header={t('tabs.health-services')}>
            <DoctorList />
          </TabPanel>
        )}

        {isGymServiceExist && hasGym && (
          <TabPanel header={t('tabs.gym-services')}>
            <GymFilteredList />
          </TabPanel>
        )}

        {isCoiffeurServiceExist && hasCoiffeur && (
          <TabPanel header={t('tabs.coiffeur-salon')}>
            <CoiffeurSalonList />
          </TabPanel>
        )}

        {isLactationServiceExist && hasLactation && (
          <TabPanel header={t('tabs.lactation-room')}>
            <LactationRoomList />
          </TabPanel>
        )}

        {isParkAreaServiceExist && hasParkArea && (
          <TabPanel header={t('tabs.park-area')}>
            <ParkAreaList redirectCommon={redirectCommon} />
          </TabPanel>
        )}

        {isMeetingServiceExist && hasMeeting && (
          <TabPanel header={t('tabs.meeting-room')}>
            <MeetingRoomList />
          </TabPanel>
        )}

        {isPoolVehicleServiceExist && hasPoolVehicle && (
          <TabPanel header={t('tabs.pool-vehicle')}>
            <PoolVehicleList />
          </TabPanel>
        )}
      </TabView>
    </InnerTabWrapper>
  )
}

const InnerTabWrapper = styled.div`
  margin: -16px;
`
